/** @module A text field with a dropdown: pick a suggestion or type something of your own. */
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { filterGroups, mergeGroups, type OptionGroup } from "@/lib/suggestions";

export function ComboInput({
  label,
  value,
  onChange,
  groups,
  placeholder,
  maxLength,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** E.g. "Zuletzt verwendet" first, then presets; duplicates are dropped. */
  groups: OptionGroup[];
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  // Null: show everything (opened by the arrow); text: filter by what was typed.
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const shown = filterGroups(mergeGroups(groups), query);
  const flat = shown.flatMap((group) => group.options);
  const hasOptions = mergeGroups(groups).length > 0;

  const close = () => {
    setOpen(false);
    setQuery(null);
    setActive(-1);
  };
  const pick = (option: string) => {
    onChange(option);
    close();
  };

  return (
    <div className="field relative">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <span className="relative block">
        <input
          id={id}
          role="combobox"
          aria-expanded={open && flat.length > 0}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${id}-option-${String(active)}` : undefined}
          autoComplete="off"
          className="input w-full pr-11"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          required={required}
          // Options and the arrow keep the focus (mouse down is prevented), so a blur means away.
          onBlur={close}
          onChange={(event) => {
            onChange(event.target.value);
            setQuery(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
              setActive((current) => Math.min(current + 1, flat.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive((current) => Math.max(current - 1, 0));
            } else if (event.key === "Enter" && open && active >= 0 && flat[active]) {
              event.preventDefault();
              pick(flat[active]);
            } else if (event.key === "Escape" && open) {
              // The sheet around would close too; one Escape closes only the list.
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
        />
        {hasOptions ? (
          <button
            type="button"
            tabIndex={-1}
            className="absolute top-1/2 right-1 grid size-10 -translate-y-1/2 place-items-center text-(--text-muted)"
            aria-label={t("common.showSuggestions", { label })}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              if (open) {
                close();
              } else {
                setQuery(null);
                setOpen(true);
              }
            }}
          >
            <ChevronDown className="size-5" />
          </button>
        ) : null}
      </span>
      {open && flat.length > 0 ? (
        <div
          id={`${id}-list`}
          role="listbox"
          aria-label={label}
          className="absolute top-full right-0 left-0 z-40 mt-1 max-h-64 overflow-y-auto rounded-[var(--radius-md)] border border-(--border) bg-(--surface) py-1 shadow-[var(--shadow-3)]"
        >
          {shown.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <span className="t-label block px-3 pt-2 pb-1" aria-hidden="true">
                {group.label}
              </span>
              {group.options.map((option) => {
                const index = flat.indexOf(option);
                return (
                  <div
                    key={option}
                    id={`${id}-option-${String(index)}`}
                    role="option"
                    tabIndex={-1}
                    aria-selected={index === active}
                    className={`flex min-h-11 cursor-pointer items-center px-3 ${
                      index === active ? "bg-(--surface-2)" : ""
                    } ${option === value ? "font-semibold" : ""}`}
                    onMouseDown={(event) => {
                      // Pick before the field blurs; a tap on a phone sends this too.
                      event.preventDefault();
                      pick(option);
                    }}
                  >
                    {option}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
